/**
 * Curated catalog of map-marker glyphs and the single source of truth mapping a
 * stored icon key → Lucide component. Both the marker renderer
 * (`lucide-marker-icons.ts`) and the `IconPicker` read this, so a new glyph is
 * added in exactly one place. The `key` is the stable value persisted in a
 * location's `icon` / a track's `props.icon`.
 */

import {
  Satellite,
  Plane,
  Ship,
  Flame,
  Rocket,
  Rss,
  Cctv,
  DoorOpen,
  IdCard,
  Building2,
  Landmark,
  MapPin,
  Milestone,
  Target,
  Flag,
  Crosshair,
  Route,
  TriangleAlert,
  Construction,
  Ban,
  ShieldAlert,
  Radiation,
  Biohazard,
  Bomb,
  Zap,
  Car,
  Truck,
  Bus,
  Sailboat,
  Fuel,
  User,
  Users,
  ShieldCheck,
  UserCheck,
  Warehouse,
  Factory,
  Home,
  Tent,
  Cross,
  Hospital,
  Ambulance,
  Radio,
  RadioTower,
  Antenna,
  type LucideIcon,
} from 'lucide-react';

/** Grouping used to lay the picker out in labelled sections. */
export type MarkerIconCategory =
  | 'Navigation'
  | 'Hazards'
  | 'Vehicles'
  | 'Personnel'
  | 'Facilities'
  | 'Medical'
  | 'Comms'
  | 'Feeds';

/** A pickable marker glyph. */
export interface MarkerIcon {
  /** Stable id persisted in `location.icon` / `track.props.icon`. */
  key: string;
  /** Human label shown in the picker. */
  label: string;
  category: MarkerIconCategory;
  Icon: LucideIcon;
}

/**
 * The catalog. `Feeds` entries back live-feed layer glyphs and are excluded from
 * the user picker (see `pickableMarkerIcons`); every other entry is pickable.
 */
export const MARKER_ICON_CATALOG: readonly MarkerIcon[] = [
  { key: 'location', label: 'Location', category: 'Navigation', Icon: MapPin },
  { key: 'waypoint', label: 'Waypoint', category: 'Navigation', Icon: Milestone },
  { key: 'objective', label: 'Objective', category: 'Navigation', Icon: Target },
  { key: 'rally', label: 'Rally Point', category: 'Navigation', Icon: Flag },
  { key: 'checkpoint', label: 'Checkpoint', category: 'Navigation', Icon: Crosshair },
  { key: 'route', label: 'Route', category: 'Navigation', Icon: Route },

  { key: 'hazard', label: 'Hazard', category: 'Hazards', Icon: TriangleAlert },
  { key: 'roadblock', label: 'Roadblock', category: 'Hazards', Icon: Construction },
  { key: 'closed', label: 'Closed', category: 'Hazards', Icon: Ban },
  { key: 'danger', label: 'Danger Zone', category: 'Hazards', Icon: ShieldAlert },
  { key: 'radiation', label: 'Radiation', category: 'Hazards', Icon: Radiation },
  { key: 'biohazard', label: 'Biohazard', category: 'Hazards', Icon: Biohazard },
  { key: 'explosive', label: 'Explosive', category: 'Hazards', Icon: Bomb },
  { key: 'electrical', label: 'Electrical', category: 'Hazards', Icon: Zap },

  { key: 'vehicle', label: 'Vehicle', category: 'Vehicles', Icon: Car },
  { key: 'truck', label: 'Truck', category: 'Vehicles', Icon: Truck },
  { key: 'bus', label: 'Bus', category: 'Vehicles', Icon: Bus },
  { key: 'boat', label: 'Boat', category: 'Vehicles', Icon: Sailboat },
  { key: 'fuel', label: 'Fuel', category: 'Vehicles', Icon: Fuel },

  { key: 'personnel', label: 'Personnel', category: 'Personnel', Icon: User },
  { key: 'team', label: 'Team', category: 'Personnel', Icon: Users },
  { key: 'friendly', label: 'Friendly', category: 'Personnel', Icon: ShieldCheck },
  { key: 'guard', label: 'Guard', category: 'Personnel', Icon: UserCheck },

  { key: 'facility', label: 'Facility', category: 'Facilities', Icon: Building2 },
  { key: 'gate', label: 'Gate', category: 'Facilities', Icon: DoorOpen },
  { key: 'pass-id', label: 'Pass & ID', category: 'Facilities', Icon: IdCard },
  { key: 'landmark', label: 'Landmark', category: 'Facilities', Icon: Landmark },
  { key: 'warehouse', label: 'Warehouse', category: 'Facilities', Icon: Warehouse },
  { key: 'factory', label: 'Factory', category: 'Facilities', Icon: Factory },
  { key: 'housing', label: 'Housing', category: 'Facilities', Icon: Home },
  { key: 'camp', label: 'Camp', category: 'Facilities', Icon: Tent },
  { key: 'fire-station', label: 'Fire Station', category: 'Facilities', Icon: Flame },

  { key: 'medical', label: 'Medical', category: 'Medical', Icon: Cross },
  { key: 'hospital', label: 'Hospital', category: 'Medical', Icon: Hospital },
  { key: 'ambulance', label: 'Ambulance', category: 'Medical', Icon: Ambulance },

  { key: 'comms', label: 'Comms', category: 'Comms', Icon: Radio },
  { key: 'tower', label: 'Comms Tower', category: 'Comms', Icon: RadioTower },
  { key: 'antenna', label: 'Antenna', category: 'Comms', Icon: Antenna },

  { key: 'satellite', label: 'Satellite', category: 'Feeds', Icon: Satellite },
  { key: 'aircraft', label: 'Aircraft', category: 'Feeds', Icon: Plane },
  { key: 'vessel', label: 'Vessel', category: 'Feeds', Icon: Ship },
  { key: 'fire', label: 'Fire', category: 'Feeds', Icon: Flame },
  { key: 'launch', label: 'Launch', category: 'Feeds', Icon: Rocket },
  { key: 'news', label: 'News', category: 'Feeds', Icon: Rss },
  { key: 'cctv', label: 'CCTV', category: 'Feeds', Icon: Cctv },
];

/** Icon component by stored key — the lookup the marker pipeline uses. */
export const MARKER_ICON_BY_KEY: ReadonlyMap<string, LucideIcon> = new Map(
  MARKER_ICON_CATALOG.map((icon) => [icon.key, icon.Icon]),
);

/** Catalog entries a user may assign to a location (excludes live-feed glyphs). */
export const pickableMarkerIcons: readonly MarkerIcon[] = MARKER_ICON_CATALOG.filter(
  (icon) => icon.category !== 'Feeds',
);
